import { useContext } from 'react';
import { store } from 'components/Store';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';

const useOrganizationInfoDescriptionFunctions = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { team } = state ?? {};
  const { generalApiCall } = useGeneralApiCall();

  const updateOrganizationInfoDescription = async ({ value, keywordListId, teamId }) => {
    let method = 'post';
    let pathname = keywordListId
      ? `/api/keyword-list/client-organisation-description/${keywordListId}`
      : `/api/team/update-organisation-description`;
    let requestProperties = { description: value };
    if (teamId) {
      requestProperties = { ...requestProperties, teamId: teamId };
    }
    const result = await generalApiCall({
      method,
      pathname,
      requestProperties,
      needsAuthentication: true,
    });
    if (result) {
      if (!keywordListId) {
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'team',
          value: { ...team, organisationDescription: result?.organisationDescription },
        });
      }
      return result;
    }
  };

  const removeAndUpdateOrganizationDescription = async ({ value, keywordListId, teamId }) => {
    let removeResult = await removeOrganizationDescription({ keywordListId, teamId });
    if (removeResult.status === 200) {
      let updateResult = await updateOrganizationInfoDescription({
        value,
        keywordListId,
        teamId,
      });
      return updateResult;
    }
  };

  const removeAndUpdateTeamAdminOrganizationDescription = async ({ value, teamId, keywordListId }) => {
    let removeResult = await removeTeamAdminOrganizationDescription({
      teamId,
      keywordListId,
    });
    if (removeResult.status === 200) {
      let updateResult = await updateTeamAdminDescription({
        value,
        teamId,
        keywordListId,
      });
      return updateResult;
    }
  };

  const removeTeamAdminOrganizationDescription = async ({ teamId, keywordListId }) => {
    let method = 'delete';
    let pathname = keywordListId
      ? `/api/admin/client-organisation-description/${keywordListId}`
      : `/api/admin/organisation-description/${teamId}`;
    const result = await generalApiCall({
      method,
      pathname,
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    if (result?.status === 200) {
      return result;
    }
  };

  const removeOrganizationDescription = async ({ keywordListId, teamId }) => {
    let method = keywordListId ? 'delete' : 'post';
    let pathname = keywordListId
      ? `/api/keyword-list/client-organisation-description/${keywordListId}`
      : `/api/team/remove-organisation-description${teamId ? `?teamId=${teamId}` : ''}`;
    const result = await generalApiCall({
      method,
      pathname,
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    if (result?.status === 200) {
      if (!keywordListId) {
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'team',
          value: { ...team, organisationDescription: result?.organisationDescription },
        });
      }
      return result;
    }
  };

  const getClientDescription = async ({ keywordListId }) => {
    let method = 'get';
    let pathname = `/api/keyword-list/client-organisation-description/${keywordListId}`;
    const result = await generalApiCall({
      method,
      pathname,
      needsAuthentication: true,
    });
    if (result) {
      return result;
    }
  };

  const updateTeamAdminDescription = async ({ value, teamId, keywordListId }) => {
    let method = 'post';
    let pathname = keywordListId
      ? `/api/admin/client-organisation-description/${keywordListId}`
      : `/api/admin/organisation-description/${teamId}`;
    let requestProperties = { description: value };
    const result = await generalApiCall({
      method,
      pathname,
      requestProperties,
      needsAuthentication: true,
    });
    if (result) {
      return result;
    }
  };

  const removeOrganizationBriefingInstructions = async ({ teamId }) => {
    let method = 'delete';
    let pathname = `/api/admin/briefing-fine-tuning-instructions/${teamId}`;
    const result = await generalApiCall({
      method,
      pathname,
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    if (result?.status === 200) {
      return result;
    }
  };

  const removeAndUpdateOrganizationBriefingInstructions = async ({ value, teamId }) => {
    let removeResult = await removeOrganizationBriefingInstructions({ teamId });
    if (removeResult.status === 200) {
      let updateResult = await updateBriefingInstructions({ value, teamId });
      return updateResult;
    }
  };

  const updateBriefingInstructions = async ({ value, teamId }) => {
    let method = 'post';
    let pathname = `/api/admin/briefing-fine-tuning-instructions/${teamId}`;
    let requestProperties = { briefingFineTuningInstructions: value };
    const result = await generalApiCall({
      method,
      pathname,
      requestProperties,
      needsAuthentication: true,
    });
    if (result) {
      return result;
    }
  };

  const generateOrganisationDescription = async ({ source, teamName, clientWebsite, teamId, adminPage }) => {
    try {
      const pathname = `/api/${adminPage ? 'admin' : 'team'}/generate-organisation-description${
        teamId ? `/${teamId}` : ''
      }${!!teamName ? `?teamName=${encodeURIComponent(teamName)}` : ''}${
        !!clientWebsite ? `?teamWebsite=${encodeURIComponent(clientWebsite)}` : ''
      }`;
      const results = await generalApiCall({
        pathname,
        method: 'get',
        needsAuthentication: true,
        notShowErrorMessage: true,
        requestSource: source,
      });
      return results;
    } catch (error) {}
  };

  return {
    updateOrganizationInfoDescription,
    removeOrganizationDescription,
    getClientDescription,
    removeAndUpdateOrganizationDescription,
    updateTeamAdminDescription,
    removeAndUpdateTeamAdminOrganizationDescription,
    removeTeamAdminOrganizationDescription,
    removeOrganizationBriefingInstructions,
    removeAndUpdateOrganizationBriefingInstructions,
    updateBriefingInstructions,
    generateOrganisationDescription,
  };
};

export default useOrganizationInfoDescriptionFunctions;
